import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Download, Plus, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { invoicesApi, type DiscountType, type InvoicePayload, type InvoiceStatus, type InvoiceUpdatePayload, type UpcomingAppointment } from '../../api/invoices'
import { servicesApi } from '../../api/services'
import { categoriesApi } from '../../api/categories'
import { includedServicesApi } from '../../api/includedServices'
import { companySettingsApi } from '../../api/settings'
import { composeFooterNote, pickText } from '../../lib/footerNote'
import { leadsApi } from '../../api/leads'
import { DropdownSelect } from '../../components/ui/DropdownSelect'
import { Modal } from '../../components/ui/Modal'
import { StatusBadge } from '../../components/ui/StatusBadge'
import { INVOICE_STATUS_LABELS, INVOICE_NEXT_STATUSES } from '../../lib/invoiceStatus'
import { useAuthStore } from '../../store/auth'
import { fullName } from '../../lib/utils'
import { timeSlots, endTimeOptions, toLocalDateString } from '../../lib/scheduling'

const SHARED_IN_CHANNELS: { value: string; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
]
/** DD/MM/YYYY HH:MM, or DD/MM/YYYY (all day) — the withdraw popup's visit list. */
const visitLabel = (a: UpcomingAppointment): string => {
  if (a.isAllDay) {
    const [y, m, d] = (a.allDayDate ?? '').split('-')
    return `${d}/${m}/${y} (all day)`
  }
  const start = new Date(a.startAt!)
  return `${start.toLocaleDateString('en-GB')} ${start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}

const DISCOUNT_OPTIONS = [
  { value: '', label: 'No discount' },
  { value: 'percent', label: '%' },
  { value: 'fixed', label: 'Fixed' },
]

// Pick a service name in the proforma's language, falling back to the other
// language when the preferred one is empty — mirrors the PDF's pickName so the
// builder and the rendered document always agree.
const pickName = (en: string | null | undefined, it: string | null | undefined, lang: 'en' | 'it'): string => {
  const primary = lang === 'it' ? it : en
  const secondary = lang === 'it' ? en : it
  return primary && primary.trim() ? primary : (secondary ?? '')
}

interface LineState {
  key: number
  serviceId: string | null
  name: string
  unitPrice: string
  quantity: string
  discountType: DiscountType | ''
  discountValue: string
  categoryId: string
}

let keySeq = 1

export function InvoiceBuilder() {
  const { companyId = '', invoiceId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  const isSales = user?.role === 'sales_user'
  const isAdmin = user?.role === 'administrator'
  const isOwnerOrAdmin = user?.role === 'company_owner' || user?.role === 'administrator'
  const canEditPrices = !isSales || (user as any)?.canEditServicePrices === true

  const leadIdParam = searchParams.get('leadId') ?? undefined
  const dealIdParam = searchParams.get('dealId') ?? undefined

  const [linkedLeadId, setLinkedLeadId] = useState<string>(leadIdParam ?? '')
  const [linkedDealId] = useState<string>(dealIdParam ?? '')
  const [patientName, setPatientName] = useState('')
  const [patientPhone, setPatientPhone] = useState('')
  const [patientEmail, setPatientEmail] = useState('')
  const [showManual, setShowManual] = useState(false)
  const [leadLocked, setLeadLocked] = useState(false)

  const [notes, setNotes] = useState('')
  const [internalNotes, setInternalNotes] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [discountType, setDiscountType] = useState<DiscountType | ''>('')
  const [discountValue, setDiscountValue] = useState('')
  const [lines, setLines] = useState<LineState[]>([])
  const [status, setStatus] = useState<InvoiceStatus>('draft')
  const [sharedIn, setSharedIn] = useState<string>('')
  const [loaded, setLoaded] = useState(!invoiceId)
  const [language, setLanguage] = useState<'en' | 'it'>('it')
  const [includedIds, setIncludedIds] = useState<string[]>([])

  const [pendingStatus, setPendingStatus] = useState<string | null>(null)
  const [pendingSharedIn, setPendingSharedIn] = useState('')
  // Booking captured when moving a proforma to "accepted" — the clinic visit is
  // created with the status change, so this popup cannot be skipped.
  const [apptDate, setApptDate] = useState('')
  const [apptStart, setApptStart] = useState('')
  const [apptEnd, setApptEnd] = useState('')
  const [apptNote, setApptNote] = useState('')

  const { data: services = [] } = useQuery({
    queryKey: ['services', companyId],
    queryFn: () => servicesApi.list(companyId),
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', companyId, { includeInactive: false }],
    queryFn: () => categoriesApi.list(companyId),
  })
  const categoryOptions = categories.map(c => ({ value: c.id, label: c.name || c.nameIt || '—' }))

  const { data: includedServices = [] } = useQuery({
    queryKey: ['included-services', companyId],
    queryFn: () => includedServicesApi.list(companyId),
  })

  const { data: companySettings } = useQuery({
    queryKey: ['company-settings', companyId],
    queryFn: () => companySettingsApi.getGeneral(companyId).then(r => r.data),
    staleTime: 5 * 60_000,
  })

  const { data: existing } = useQuery({
    queryKey: ['invoice', companyId, invoiceId],
    queryFn: () => invoicesApi.get(companyId, invoiceId!),
    enabled: !!invoiceId,
  })

  const isDealLinked = !!linkedDealId || !!existing?.dealId

  const [leadSearch, setLeadSearch] = useState('')
  const [debouncedLeadSearch, setDebouncedLeadSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedLeadSearch(leadSearch), 300)
    return () => clearTimeout(t)
  }, [leadSearch])

  const { data: leadsData } = useQuery({
    queryKey: ['leads-picker', companyId, debouncedLeadSearch],
    queryFn: () => leadsApi.list(companyId, { search: debouncedLeadSearch || undefined, limit: 20 } as any).then((r: any) => {
      const d = r.data ?? r
      return Array.isArray(d) ? d : (d?.data ?? [])
    }),
    staleTime: 30_000,
    enabled: !leadLocked && !isDealLinked,
  })
  const leadsArray: any[] = Array.isArray(leadsData) ? leadsData : []
  const leadOptions = useMemo(() => leadsArray.map((l: any) => ({
    value: l.id,
    label: [l.firstName, l.lastName].filter(Boolean).join(' ') + (l.phone ? ` · ${l.phone}` : ''),
  })), [leadsArray])

  // Fetch linked lead for tooltip (only when locked and we have an id)
  const { data: linkedLeadData } = useQuery({
    queryKey: ['lead', companyId, linkedLeadId],
    queryFn: () => leadsApi.get(companyId, linkedLeadId).then((r: any) => r.data ?? r),
    enabled: !!(leadLocked && linkedLeadId),
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (!existing) return
    setPatientName(existing.patientName)
    setPatientPhone(existing.patientPhone ?? '')
    setPatientEmail(existing.patientEmail ?? '')
    setNotes(existing.notes ?? '')
    setInternalNotes((existing as any).internalNotes ?? '')
    setValidUntil(existing.validUntil ? String(existing.validUntil).slice(0, 10) : '')
    setDiscountType((existing.discountType ?? '') as DiscountType | '')
    setDiscountValue(existing.discountValue != null ? String(Number(existing.discountValue)) : '')
    setStatus(existing.status)
    setSharedIn((existing as any).sharedIn ?? '')
    setLeadLocked(true)
    if (existing.leadId) setLinkedLeadId(existing.leadId)
    setLanguage((existing as any).language ?? 'en')
    setIncludedIds(((existing as any).includedServices ?? []).map((s: { id: string }) => s.id))
    setLines((existing.items ?? []).map(it => ({
      key: keySeq++, serviceId: it.serviceId, name: it.name,
      unitPrice: String(Number(it.unitPrice)), quantity: String(it.quantity),
      discountType: (it.discountType ?? '') as DiscountType | '',
      discountValue: it.discountValue != null ? String(Number(it.discountValue)) : '',
      categoryId: (it as any).categoryId ?? '',
    })))
    setLoaded(true)
  }, [existing])

  useEffect(() => {
    if (invoiceId || !leadIdParam) return
    leadsApi.get(companyId, leadIdParam).then((r: any) => {
      const lead = r.data ?? r
      setPatientName(v => v || [lead.firstName, lead.lastName].filter(Boolean).join(' '))
      setPatientPhone(v => v || lead.phone || '')
      setPatientEmail(v => v || lead.email || '')
    }).catch(() => {})
  }, [companyId, leadIdParam, invoiceId])

  const handleLeadSelect = (leadId: string) => {
    setLinkedLeadId(leadId)
    const lead = leadsArray.find((l: any) => l.id === leadId)
    if (lead) {
      setPatientName([lead.firstName, lead.lastName].filter(Boolean).join(' '))
      setPatientPhone(lead.phone ?? '')
      setPatientEmail(lead.email ?? '')
    }
  }

  // Administrators may also correct a proforma after it was shared (backend enforces the same rule).
  const adminEditingSent = isAdmin && status === 'sent'
  const contentEditable = ['draft', 'created'].includes(status) || adminEditingSent
  const isDraft = status === 'draft'
  const isCreated = status === 'created'
  const nextOpts = (INVOICE_NEXT_STATUSES[status] ?? []).map(s => ({ value: s, label: INVOICE_STATUS_LABELS[s] }))

  const payload = useMemo<InvoicePayload>(() => ({
    ...(invoiceId ? {} : linkedDealId
      ? { dealId: linkedDealId }
      : { leadId: linkedLeadId || undefined }),
    patientName: patientName.trim() || '—',
    patientPhone: patientPhone.trim() || undefined,
    patientEmail: patientEmail.trim() || undefined,
    discountType: (discountType || undefined) as DiscountType | undefined,
    discountValue: discountValue !== '' ? Number(discountValue) : undefined,
    notes: notes.trim() || undefined,
    validUntil: validUntil || undefined,
    language,
    includedServiceIds: includedIds,
    items: lines.map(l => ({
      serviceId: l.serviceId ?? undefined,
      name: l.serviceId ? undefined : l.name,
      unitPrice: l.unitPrice !== '' ? Number(l.unitPrice) : undefined,
      quantity: Number(l.quantity) || 1,
      discountType: (l.discountType || undefined) as DiscountType | undefined,
      discountValue: l.discountValue !== '' ? Number(l.discountValue) : undefined,
      categoryId: l.categoryId || undefined,
    })),
  }), [invoiceId, linkedLeadId, linkedDealId, patientName, patientPhone, patientEmail, discountType, discountValue, notes, validUntil, lines, language, includedIds])

  const updatePayload = useMemo<InvoiceUpdatePayload>(() => ({
    discountType: payload.discountType,
    discountValue: payload.discountValue,
    notes: payload.notes,
    validUntil: payload.validUntil,
    language: payload.language,
    includedServiceIds: payload.includedServiceIds,
    internalNotes,
    items: payload.items,
  }), [payload, internalNotes])

  const invoicing = (companySettings as any)?.settings?.invoicing ?? {}
  const staticNote = language === 'it'
    ? (invoicing.footerNoteIt || invoicing.footerNote || '')
    : (invoicing.footerNoteEn || invoicing.footerNote || '')

  const composedFooter = useMemo(
    () => composeFooterNote(staticNote, includedServices.filter(s => includedIds.includes(s.id)), language),
    [staticNote, includedServices, includedIds, language],
  )

  const previewable = loaded && lines.length > 0
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const previewUrlRef = useRef<string | null>(null)
  const previewSeq = useRef(0)
  const previewTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (!previewable) { setPreviewUrl(null); return }
    clearTimeout(previewTimer.current)
    const seq = ++previewSeq.current
    previewTimer.current = setTimeout(async () => {
      try {
        const blob = await invoicesApi.preview(companyId, { ...payload, invoiceId: invoiceId ?? undefined })
        if (seq !== previewSeq.current) return
        setPreviewUrl(old => {
          if (old) URL.revokeObjectURL(old)
          const next = URL.createObjectURL(blob)
          previewUrlRef.current = next
          return next
        })
      } catch { /* keep last good preview */ }
    }, 600)
    return () => clearTimeout(previewTimer.current)
  }, [companyId, payload, previewable, invoiceId])

  useEffect(() => () => { if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current) }, [])

  const addCatalogLine = (serviceId: string) => {
    const svc = services.find(s => s.id === serviceId)
    if (!svc) return
    setLines(ls => [...ls, { key: keySeq++, serviceId: svc.id, name: svc.name ?? svc.nameIt ?? '', unitPrice: String(Number(svc.price)), quantity: '1', discountType: '', discountValue: '', categoryId: '' }])
  }
  const addCustomLine = () => setLines(ls => [...ls, { key: keySeq++, serviceId: null, name: '', unitPrice: '', quantity: '1', discountType: '', discountValue: '', categoryId: '' }])
  const patchLine = (key: number, patch: Partial<LineState>) => setLines(ls => ls.map(l => (l.key === key ? { ...l, ...patch } : l)))
  const removeLine = (key: number) => setLines(ls => ls.filter(l => l.key !== key))

  const saveMut = useMutation({
    mutationFn: () => invoicesApi.update(companyId, invoiceId!, updatePayload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', companyId, invoiceId] })
      qc.invalidateQueries({ queryKey: ['invoices', companyId] })
    },
  })

  const saveWithStatus = useMutation({
    mutationFn: async (targetStatus: InvoiceStatus) => {
      let inv: any
      if (invoiceId) {
        inv = await invoicesApi.update(companyId, invoiceId, updatePayload)
      } else {
        inv = await invoicesApi.create(companyId, { ...payload, internalNotes } as any)
      }
      if (targetStatus === 'created') {
        inv = await invoicesApi.updateStatus(companyId, inv.id, { status: 'created' })
      }
      return inv
    },
    onSuccess: inv => {
      setLeadLocked(true)
      setStatus(inv.status)
      qc.invalidateQueries({ queryKey: ['invoices', companyId] })
      qc.invalidateQueries({ queryKey: ['invoice', companyId, inv.id] })
      if (!invoiceId) navigate(`/company/${companyId}/invoices/${inv.id}/edit`, { replace: true })
    },
  })

  const statusMut = useMutation({
    mutationFn: (dto: { status: InvoiceStatus; sharedIn?: string }) =>
      invoicesApi.updateStatus(companyId, invoiceId!, dto),
    onSuccess: inv => {
      setStatus(inv.status)
      if ((inv as any).sharedIn) setSharedIn((inv as any).sharedIn)
      qc.invalidateQueries({ queryKey: ['invoice', companyId, invoiceId] })
      qc.invalidateQueries({ queryKey: ['invoices', companyId] })
      // Accepting books a visit, so the calendar is now stale too.
      qc.invalidateQueries({ queryKey: ['appointments', companyId] })
      resetPending()
    },
    // The server re-checks the booking (a slot can fall into the past while the
    // popup sits open). Keep the popup up with its values so the date can be
    // corrected rather than re-entered.
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(Array.isArray(msg) ? msg.join(', ') : (msg ?? 'Could not change the status'))
    },
  })

  // ── Acceptance booking ──────────────────────────────────────────────────────
  // Today in the browser's own timezone; the date input refuses anything earlier.
  const todayStr = toLocalDateString(new Date())
  // On today's date the times already gone are dropped, so the picker can only
  // produce a future slot. Other days keep the full 24h list.
  const startSlotOptions = useMemo(() => {
    const all = timeSlots(15).map(t => ({ value: t, label: t }))
    if (apptDate !== todayStr) return all
    const now = new Date()
    const minutesNow = now.getHours() * 60 + now.getMinutes()
    return all.filter(({ value }) => {
      const [h, m] = value.split(':').map(Number)
      return h * 60 + m > minutesNow
    })
  }, [apptDate, todayStr])

  const apptStartIso = apptDate && apptStart
    ? new Date(`${apptDate}T${apptStart}:00`).toISOString()
    : null
  // Guards the button even if a stale slot survives a date change.
  const apptIsFuture = !!apptStartIso && new Date(apptStartIso).getTime() > Date.now()
  // The end time is optional, but when given it must be after the start — the
  // server rejects anything else, so catch it before the round-trip.
  const apptEndValid = !apptEnd
    || (!!apptStartIso && new Date(`${apptEnd}:00`).getTime() > new Date(apptStartIso).getTime())
  const apptValid = apptIsFuture && apptEndValid

  const resetPending = () => {
    setPendingStatus(null)
    setPendingSharedIn('')
    setApptDate('')
    setApptStart('')
    setApptEnd('')
    setApptNote('')
  }

  const requestStatusChange = (newStatus: string) => { resetPending(); setPendingStatus(newStatus) }
  const confirmStatusChange = () => {
    if (!pendingStatus) return
    if (pendingStatus === 'accepted' && !apptValid) return
    statusMut.mutate({
      status: pendingStatus as InvoiceStatus,
      sharedIn: pendingStatus === 'sent' ? pendingSharedIn : undefined,
      appointment: pendingStatus === 'accepted' && apptStartIso
        ? {
            startAt: apptStartIso,
            endAt: apptEnd ? new Date(`${apptEnd}:00`).toISOString() : null,
            note: apptNote.trim() || undefined,
          }
        : undefined,
    })
  }

  const download = async () => {
    if (!invoiceId) return
    const blob = await invoicesApi.pdfBlob(companyId, invoiceId)
    const url = URL.createObjectURL(blob)
    Object.assign(document.createElement('a'), { href: url, download: `${existing?.number ?? 'proforma'}.pdf` }).click()
    URL.revokeObjectURL(url)
  }

  // When the lead is already locked (existing saved invoice), the linkage was
  // validated at creation — bypass the check. For new invoices, enforce per role.
  const hasValidLink = leadLocked ? true : (isDealLinked
    ? true
    : isSales ? !!linkedLeadId : (!!linkedLeadId || showManual))
  const canSave = hasValidLink && patientName.trim() !== '' && lines.length > 0
    && lines.every(l => (l.serviceId || l.name.trim()) && Number(l.quantity) >= 1 && l.unitPrice !== '')
  const serviceOptions = services.map(s => ({ value: s.id, label: `${pickName(s.name, s.nameIt, language)} — ${Number(s.price).toFixed(2)}` }))
  const displayServiceName = (line: { serviceId: string | null; name: string }) => {
    const s = line.serviceId ? services.find(x => x.id === line.serviceId) : undefined
    return s ? pickName(s.name, s.nameIt, language) : line.name
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* LEFT: form */}
      <div className="w-1/2 flex flex-col overflow-hidden border-r">

        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white flex-shrink-0">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/company/${companyId}/invoices`)}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <StatusBadge status={status} className="text-xs" />
            {invoiceId && nextOpts.length > 0 && (
              <DropdownSelect value="" placeholder="Change status…" options={nextOpts} onChange={requestStatusChange} />
            )}
            {invoiceId && (
              <button className="btn btn-ghost btn-sm" onClick={download}>
                <Download className="w-4 h-4" /> PDF
              </button>
            )}
            {(isCreated || adminEditingSent) && invoiceId && (
              <button className="btn btn-secondary btn-sm" disabled={!canSave || saveMut.isPending} onClick={() => saveMut.mutate()}>
                {saveMut.isPending ? 'Saving…' : 'Save'}
              </button>
            )}
            {isDraft && (
              <>
                <button className="btn btn-secondary btn-sm" disabled={!canSave || saveWithStatus.isPending} onClick={() => saveWithStatus.mutate('draft')}>
                  Save as draft
                </button>
                <button className="btn btn-primary btn-sm" disabled={!canSave || saveWithStatus.isPending} onClick={() => saveWithStatus.mutate('created')}>
                  {saveWithStatus.isPending ? 'Saving…' : 'Create'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Title + language pills */}
        <div className="px-4 pt-3 pb-1 flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-base font-semibold text-gray-900">{existing?.number ?? 'New proforma'}</h1>
            <div className="flex items-center gap-1">
              {([['it', '🇮🇹 IT', 'bg-green-50 text-green-700 border-green-300'],
                 ['en', '🇬🇧 EN', 'bg-blue-50 text-blue-700 border-blue-300']] as const).map(([lng, lbl, active]) => (
                <button key={lng} type="button"
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${language === lng ? active : 'bg-white text-gray-400 border-gray-200'}`}
                  onClick={() => {
                    setLanguage(lng)
                    if (invoiceId) invoicesApi.setLanguage(companyId, invoiceId, lng).catch(() => {})
                  }}>{lbl}</button>
              ))}
            </div>
          </div>
          {!contentEditable && (
            <p className="text-xs text-amber-600 mt-0.5">
              Status is <strong>{INVOICE_STATUS_LABELS[status]}</strong> — content is locked.
              {sharedIn && <> Shared via <strong className="capitalize">{sharedIn}</strong>.</>}
            </p>
          )}
          {adminEditingSent && (
            <p className="text-xs text-amber-600 mt-0.5">
              Status is <strong>{INVOICE_STATUS_LABELS[status]}</strong> — editing as administrator.
              {sharedIn && <> Shared via <strong className="capitalize">{sharedIn}</strong>.</>} The
              client may already have the original PDF.
            </p>
          )}
        </div>

        {/* Form body */}
        <div className="flex-1 overflow-y-auto px-4 pb-6">

          {/* Lead / customer section */}
          <div className="mt-3">
            {!isDealLinked && (!showManual || isSales) && (
              <div className="mb-3">
                <label className="label">Lead {isSales && <span className="text-red-500 ml-0.5">*</span>}</label>
                {leadLocked ? (
                  <div className="relative group">
                    <div className="input bg-gray-50 flex items-center gap-2 cursor-default min-h-[38px]">
                      <span className="text-gray-700 text-sm truncate">{patientName}</span>
                      {existing?.leadId && (
                        <a href={`/company/${companyId}/leads/${existing.leadId}`}
                          className="ml-auto text-primary-600 hover:underline text-xs whitespace-nowrap"
                          target="_blank" rel="noreferrer">
                          View lead ↗
                        </a>
                      )}
                      <span className="text-xs text-gray-400 whitespace-nowrap">Locked</span>
                    </div>
                    {/* Tooltip with lead details */}
                    {linkedLeadData && (
                      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
                        <p className="font-semibold text-gray-900 mb-1">
                          {fullName((linkedLeadData as any).firstName, (linkedLeadData as any).lastName)}
                        </p>
                        {(linkedLeadData as any).phone && <p className="text-gray-600">📞 {(linkedLeadData as any).phone}</p>}
                        {(linkedLeadData as any).email && <p className="text-gray-600">✉ {(linkedLeadData as any).email}</p>}
                        {(linkedLeadData as any).source && <p className="text-gray-500 mt-1">Source: {(linkedLeadData as any).source}</p>}
                        <p className="text-gray-500">Lead status: <span className="font-medium">{(linkedLeadData as any).status ?? '—'}</span></p>
                        <p className="text-gray-500">Deal: <span className="font-medium">{(linkedLeadData as any).convertedToDealId ? 'Converted' : '—'}</span></p>
                      </div>
                    )}
                  </div>
                ) : (
                  <DropdownSelect value={linkedLeadId} options={leadOptions} onChange={handleLeadSelect}
                    searchable onSearchChange={setLeadSearch} placeholder="Search lead by name or phone…" />
                )}
                {isOwnerOrAdmin && !leadLocked && (
                  <button type="button" className="text-xs text-primary-600 hover:underline mt-1" onClick={() => setShowManual(m => !m)}>
                    {showManual ? '← Use lead picker' : 'Add customer details manually'}
                  </button>
                )}
              </div>
            )}

            {(isDealLinked || (showManual && isOwnerOrAdmin)) && (
              <div>
                {isDealLinked && (
                  <div className="mb-2 text-xs text-gray-500">
                    Linked to deal
                    {(existing?.dealId ?? linkedDealId) && (
                      <a href={`/company/${companyId}/deals/${existing?.dealId ?? linkedDealId}`}
                        className="ml-1 text-primary-600 hover:underline" target="_blank" rel="noreferrer">
                        View deal ↗
                      </a>
                    )}
                  </div>
                )}
                {!leadLocked && !isDealLinked && (
                  <button type="button" className="text-xs text-primary-600 hover:underline mb-2 block" onClick={() => setShowManual(false)}>
                    ← Use lead picker
                  </button>
                )}
                <label className="label">{isDealLinked ? 'Customer name' : 'Lead name'} <span className="text-red-500">*</span></label>
                <input className="input mb-3" value={patientName} onChange={e => setPatientName(e.target.value)} disabled={leadLocked} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Phone</label>
                    <input className="input mb-3" value={patientPhone} onChange={e => setPatientPhone(e.target.value)} disabled={leadLocked} />
                  </div>
                  <div>
                    <label className="label">Email</label>
                    <input className="input mb-3" value={patientEmail} onChange={e => setPatientEmail(e.target.value)} disabled={leadLocked} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Content (locked at sent+) */}
          <fieldset disabled={!contentEditable} className="disabled:opacity-60 disabled:pointer-events-none">
            <div className="flex items-center justify-between mt-4 mb-2">
              <h2 className="text-sm font-medium text-gray-700">Services</h2>
              <div className="flex items-center gap-2">
                <DropdownSelect value="" options={serviceOptions} searchable placeholder="Add service…"
                  onChange={addCatalogLine}
                  className="min-w-[16rem]" panelClassName="min-w-[22rem]" />
                <button type="button" className="btn btn-ghost btn-sm" onClick={addCustomLine}>
                  <Plus className="w-4 h-4" /> Custom line
                </button>
              </div>
            </div>

            {lines.map(line => (
              <div key={line.key} className="border border-gray-200 rounded-md p-3 mb-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    {line.serviceId
                      ? <p className="text-sm font-medium pt-1.5">{displayServiceName(line)}</p>
                      : <input className="input" placeholder="Custom service name" value={line.name} onChange={e => patchLine(line.key, { name: e.target.value })} />}
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm text-red-600" onClick={() => removeLine(line.key)}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  <div>
                    <label className="label">Category</label>
                    <DropdownSelect value={line.categoryId} options={categoryOptions}
                      placeholder="—" searchable={categoryOptions.length > 6}
                      onChange={v => patchLine(line.key, { categoryId: v })}
                      onClear={() => patchLine(line.key, { categoryId: '' })} />
                  </div>
                  <div>
                    <label className="label">Qty</label>
                    <input className="input" type="number" min="1" step="1" value={line.quantity} onChange={e => patchLine(line.key, { quantity: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Unit price</label>
                    <input className="input" type="number" min="0" step="1" value={line.unitPrice}
                      disabled={!!line.serviceId && !canEditPrices}
                      onChange={e => patchLine(line.key, { unitPrice: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Discount</label>
                    <DropdownSelect value={line.discountType} options={DISCOUNT_OPTIONS}
                      onChange={v => patchLine(line.key, { discountType: v as DiscountType | '', ...(v === '' ? { discountValue: '' } : {}) })} />
                  </div>
                  <div>
                    <label className="label">Value</label>
                    <input className="input" type="number" min="0" step="1" value={line.discountValue}
                      disabled={!line.discountType} onChange={e => patchLine(line.key, { discountValue: e.target.value })} />
                  </div>
                </div>
              </div>
            ))}
            {!lines.length && (
              <p className="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-md mb-2">
                Add a service or a custom line to start.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="label">Invoice discount</label>
                <DropdownSelect value={discountType} options={DISCOUNT_OPTIONS}
                  onChange={v => { setDiscountType(v as DiscountType | ''); if (v === '') setDiscountValue('') }} />
              </div>
              <div>
                <label className="label">Value</label>
                <input className="input" type="number" min="0" step="1" value={discountValue} disabled={!discountType} onChange={e => setDiscountValue(e.target.value)} />
              </div>
            </div>

            <label className="label mt-3">Valid until <span className="text-xs text-gray-400">(shown on PDF)</span></label>
            <input className="input mb-1" type="date" value={validUntil}
              disabled={!contentEditable}
              onChange={e => setValidUntil(e.target.value)} />

            <label className="label mt-3">Notes <span className="text-xs text-gray-400">(printed on PDF)</span></label>
            <textarea className="input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />

            <label className="label mt-3">Internal notes <span className="text-xs text-gray-400">(not shown in PDF)</span></label>
            <textarea className="input" rows={2} value={internalNotes} onChange={e => setInternalNotes(e.target.value)} />

            <label className="label mt-3">Services included <span className="text-xs text-gray-400">(appended to the footer note)</span></label>
            {includedServices.length === 0 ? (
              <p className="text-sm text-gray-400">None configured — add them in Settings → Invoicing.</p>
            ) : (
              <div className="space-y-1.5">
                {includedServices.map(s => (
                  <label key={s.id} className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="w-3.5 h-3.5 mt-0.5" checked={includedIds.includes(s.id)}
                      onChange={e => setIncludedIds(ids => e.target.checked ? [...ids, s.id] : ids.filter(i => i !== s.id))} />
                    <span>{pickText(s.textEn, s.textIt, language)}</span>
                  </label>
                ))}
              </div>
            )}

            <label className="label mt-3">Footer note <span className="text-xs text-gray-400">(printed on PDF)</span></label>
            <textarea className="input bg-gray-50" rows={4} readOnly value={composedFooter} />
          </fieldset>

          {sharedIn && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <label className="label">Shared via</label>
              <span className="badge badge-gray capitalize">{sharedIn}</span>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: PDF preview */}
      <div className="w-1/2 bg-gray-100 flex items-center justify-center">
        {previewUrl
          ? <iframe title="Proforma preview" src={`${previewUrl}#toolbar=0`} className="w-full h-full border-0" />
          : <p className="text-sm text-gray-400 px-8 text-center">The PDF preview appears here once the proforma has at least one line.</p>}
      </div>

      {/* Status change modal */}
      <Modal
        open={!!pendingStatus}
        onClose={resetPending}
        title={pendingStatus === 'accepted' ? 'Accept proforma' : 'Change status'}
        size={pendingStatus === 'accepted' ? 'md' : 'sm'}
      >
        <div className="py-1">
          <p className="text-sm text-gray-600 mb-4">
            Move proforma to <strong>{pendingStatus ? INVOICE_STATUS_LABELS[pendingStatus as InvoiceStatus] : ''}</strong>?
            {pendingStatus && ['declined', 'withdrawn', 'paid', 'accepted'].includes(pendingStatus) && (
              <span className="block text-xs text-amber-600 mt-1">This transition cannot be reversed.</span>
            )}
          </p>
          {pendingStatus === 'withdrawn' && status === 'accepted' && !!existing?.upcomingAppointments?.length && (
            <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {existing.upcomingAppointments.length === 1 ? 'This appointment' : 'These appointments'} will be
              cancelled in the Appointments calendar:
              <ul className="mt-1 list-disc pl-5">
                {existing.upcomingAppointments.map(a => <li key={a.id}><strong>{visitLabel(a)}</strong></li>)}
              </ul>
            </div>
          )}
          {pendingStatus === 'sent' && (
            <div className="mb-4">
              <label className="label">How was this proforma shared? <span className="text-red-500">*</span></label>
              <div className="flex flex-wrap gap-2 mt-1">
                {SHARED_IN_CHANNELS.map(ch => (
                  <button
                    key={ch.value}
                    type="button"
                    onClick={() => setPendingSharedIn(ch.value)}
                    className={[
                      'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                      pendingSharedIn === ch.value
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:text-primary-600',
                    ].join(' ')}
                  >
                    {ch.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">This cannot be changed after confirming.</p>
            </div>
          )}
          {pendingStatus === 'accepted' && (
            <div className="mb-4 space-y-3">
              <p className="text-sm text-gray-700">
                When will the patient arrive at the clinic? <span className="text-red-500">*</span>
              </p>
              <div>
                <label className="label">Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  className="input"
                  value={apptDate}
                  min={todayStr}
                  onChange={e => { setApptDate(e.target.value); setApptStart(''); setApptEnd('') }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Start time <span className="text-red-500">*</span></label>
                  <DropdownSelect
                    options={startSlotOptions}
                    value={apptStart}
                    onChange={v => { setApptStart(v); setApptEnd('') }}
                    placeholder={apptDate ? '--:--' : 'Pick a date first'}
                    searchable
                  />
                </div>
                <div>
                  <label className="label">End time</label>
                  <DropdownSelect
                    options={apptStart ? endTimeOptions(apptDate, apptStart) : []}
                    value={apptEnd}
                    onChange={setApptEnd}
                    placeholder={apptStart ? '--:--' : 'Pick a start time first'}
                    searchable
                  />
                </div>
              </div>
              <div>
                <label className="label">Note</label>
                <textarea
                  className="input resize-none"
                  rows={3}
                  value={apptNote}
                  onChange={e => setApptNote(e.target.value)}
                  placeholder="Anything the clinic should know about this visit…"
                />
              </div>
              <p className="text-xs text-gray-400">
                Confirming books this visit in the Appointments calendar. Past dates and times cannot be selected.
              </p>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button className="btn btn-secondary btn-sm flex-1" onClick={resetPending}>
              Cancel
            </button>
            <button className="btn btn-primary btn-sm flex-1"
              disabled={
                (pendingStatus === 'sent' && !pendingSharedIn)
                || (pendingStatus === 'accepted' && !apptValid)
                || statusMut.isPending
              }
              onClick={confirmStatusChange}>
              {statusMut.isPending ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
